import { useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { SessionState, ScoreVisibility, GameMode } from "../../types/game";
import WebApp from "@twa-dev/sdk";
import { Dice1, Dice5, Palette, ChevronUp, ChevronDown, Users, X, UserPlus } from "lucide-react";
import Avatar from "../common/Avatar";
import AddPlayerModal from "../setup/AddPlayerModal";
import { cyrillicCode } from "../../utils/inviteCode";

interface Props {
  state: SessionState;
  token?: string;
  colorfulDice?: boolean;
  onUpdateSettings: (sv?: ScoreVisibility, gm?: GameMode) => void;
  onToggleColorfulDice: (val: boolean) => void;
  onReorderPlayers?: (ids: string[]) => void;
  onRemovePlayer?: (playerId: string) => void;
  onAddReal?: (userId: string, displayName?: string) => void;
  onAddPhantom?: (displayName: string, avatar: string) => void;
  onClose: () => void;
}

export default function AdminSettingsModal({ state, token, colorfulDice = false, onUpdateSettings, onToggleColorfulDice, onReorderPlayers, onRemovePlayer, onAddReal, onAddPhantom, onClose }: Props) {
  const { t } = useTranslation();
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const botUsername = import.meta.env.VITE_BOT_USERNAME || "vltvch_test_bot";
  
  // Use Telegram deep link when running inside Telegram, otherwise use web URL
  const isTelegram = !!(WebApp.initData?.trim() || (typeof window !== "undefined" && (window as any).Telegram?.WebApp?.initData));
  const inviteUrl = isTelegram
    ? `https://t.me/${botUsername}?startapp=${state.session.invite_code}`
    : `${window.location.origin}?code=${state.session.invite_code}`;

  const players = state.players_with_scorecards;

  const movePlayer = (index: number, dir: -1 | 1) => {
    const newOrder = players.map(p => p.player.id);
    const target = index + dir;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[index], newOrder[target]] = [newOrder[target], newOrder[index]];
    onReorderPlayers?.(newOrder);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6">
      <div className="glass-modal rounded-2xl p-6 w-full max-w-sm flex flex-col gap-6 max-h-full overflow-y-auto overscroll-contain">
        <h3 className="font-bold text-xl text-center">⚙️ {t("app.title")} Admin</h3>
        
        {/* Settings */}
        <div className="space-y-4">
          <div className="glass p-4 rounded-xl">
            <p className="text-sm text-ink/40 mb-2 font-semibold uppercase tracking-wider">{t("setup.visibilityOfScore")}</p>
            <div className="flex glass rounded-lg p-1">
              <button
                onClick={() => onUpdateSettings("realtime", undefined)}
                className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${
                  state.session.score_visibility === "realtime"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-ink/40 hover:text-ink"
                }`}
              >
                {t("setup.realtime")}
              </button>
              <button
                onClick={() => onUpdateSettings("final_only", undefined)}
                className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${
                  state.session.score_visibility === "final_only"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-ink/40 hover:text-ink"
                }`}
              >
                {t("setup.finalOnly")}
              </button>
            </div>
          </div>

          <div className="glass p-4 rounded-xl">
            <p className="text-sm text-ink/40 mb-2 font-semibold uppercase tracking-wider">{t("setup.gameMode")}</p>
            <div className="flex glass rounded-lg p-1">
              <button
                onClick={() => onUpdateSettings(undefined, "free")}
                className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all flex items-center justify-center gap-2 ${
                  state.session.game_mode === "free"
                    ? "bg-purple-600 text-white shadow-md"
                    : "text-ink/40 hover:text-ink"
                }`}
              >
                <Dice1 size={14} /> {t("setup.freeMode")}
              </button>
              <button
                onClick={() => onUpdateSettings(undefined, "strict")}
                className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all flex items-center justify-center gap-2 ${
                  state.session.game_mode === "strict"
                    ? "bg-purple-600 text-white shadow-md"
                    : "text-ink/40 hover:text-ink"
                }`}
              >
                <Dice5 size={14} /> {t("setup.virtualDiceMode")}
              </button>
            </div>
            {state.session.game_mode === "strict" && (
              <p className="text-[10px] text-purple-400 mt-2 text-center italic">
                {t("setup.virtualDiceModeDesc")}
              </p>
            )}
          </div>

          {(onReorderPlayers || onRemovePlayer || onAddReal) && (
            <div className="glass p-4 rounded-xl">
              <p className="text-sm text-ink/40 mb-2 font-semibold uppercase tracking-wider flex items-center gap-2">
                <Users size={14} /> {t("setup.playerOrder")}
              </p>
              <div className="space-y-1">
                {players.map(({ player }, i) => (
                  <div key={player.id} className="flex items-center gap-2 glass rounded-lg px-2 py-1.5">
                    <span className="text-xs text-ink/30 font-mono w-4">{i + 1}</span>
                    <Avatar src={player.avatar} size="sm" />
                    <span className="flex-1 text-sm truncate">{player.display_name}</span>
                    {onReorderPlayers && players.length > 1 && (
                      <>
                        <button
                          onClick={() => movePlayer(i, -1)}
                          disabled={i === 0}
                          className={`p-1.5 rounded-md transition-colors ${i === 0 ? "text-ink/15" : "text-ink/50 hover:text-ink hover:bg-ink/10"}`}
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          onClick={() => movePlayer(i, 1)}
                          disabled={i === players.length - 1}
                          className={`p-1.5 rounded-md transition-colors ${i === players.length - 1 ? "text-ink/15" : "text-ink/50 hover:text-ink hover:bg-ink/10"}`}
                        >
                          <ChevronDown size={16} />
                        </button>
                      </>
                    )}
                    {onRemovePlayer && !player.is_admin && (
                      <button
                        onClick={() => onRemovePlayer(player.id)}
                        className="p-1.5 rounded-md text-red-400 hover:bg-red-500/15 transition-colors"
                        title={t("setup.removePlayer", "Remove")}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {onAddReal && onAddPhantom && token && players.length < 6 && (
                <button
                  onClick={() => setShowAddPlayer(true)}
                  className="w-full mt-2 py-2.5 glass-btn rounded-lg text-sm font-semibold text-ink/70 hover:text-ink flex items-center justify-center gap-2 transition-colors"
                >
                  <UserPlus size={15} /> {t("setup.addPlayer")}
                </button>
              )}
            </div>
          )}

          <div className="glass p-4 rounded-xl">
            <p className="text-sm text-ink/50 mb-2 font-semibold uppercase tracking-wider flex items-center gap-2">
              <Palette size={14} /> {t("settings.diceColors")}
            </p>
            <div className="flex glass rounded-lg p-1">
              <button
                onClick={() => onToggleColorfulDice(false)}
                className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${
                  !colorfulDice
                    ? "bg-ink/20 text-ink shadow-md"
                    : "text-ink/40 hover:text-ink"
                }`}
              >
                {t("settings.default")}
              </button>
              <button
                onClick={() => onToggleColorfulDice(true)}
                className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${
                  colorfulDice
                    ? "bg-gradient-to-r from-rose-500 via-emerald-500 to-sky-500 text-white shadow-md"
                    : "text-ink/40 hover:text-ink"
                }`}
              >
                {t("settings.colorful")}
              </button>
            </div>
          </div>
        </div>

        {/* QR Code */}
        <div className="bg-white p-4 rounded-xl flex flex-col items-center gap-2">
          <QRCodeSVG value={inviteUrl} size={180} level="M" fgColor="#000000" bgColor="#ffffff" />
          <p className="text-gray-900 font-mono text-sm font-bold tracking-widest">
            {state.session.invite_code}
            {cyrillicCode(state.session.invite_code) !== state.session.invite_code &&
              ` · ${cyrillicCode(state.session.invite_code)}`}
          </p>
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 glass-btn rounded-xl font-bold transition-colors"
        >
          {t("game.close")}
        </button>
      </div>

      {showAddPlayer && token && onAddReal && onAddPhantom && (
        <AddPlayerModal
          token={token}
          onAddReal={(userId, name) => {
            onAddReal(userId, name);
            setShowAddPlayer(false);
          }}
          onAddPhantom={(name, avatar) => {
            onAddPhantom(name, avatar);
            setShowAddPlayer(false);
          }}
          onClose={() => setShowAddPlayer(false)}
          excludeNames={players.map((p) => p.player.display_name)}
        />
      )}
    </div>
  );
}
